import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import trainers from '../data/trainers';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Restaurant as NutritionIcon,
  Assessment as AssessmentIcon,
  FitnessCenter as FitnessIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Favorite as HeartIcon
} from '@mui/icons-material';
import Footer from '../components/layout/Footer';
import emailjs from '@emailjs/browser';

// Higher-order component to provide hooks to class component
const withHooks = (Component) => {
  return (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return <Component {...props} theme={theme} isMobile={isMobile} />;
  };
};

class ServicePricingPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openModal: false,
      formData: {
        name: '',
        healthGoals: '',
        trainerSelection: '',
        phoneNumber: '',
        bestTimeToCall: ''
      },
      isLoading: false,
      submitStatus: { type: null, message: '' },
      externalLink: 'https://varsity-fitness.exercise.com/packages/63528/purchase/'
    };
  }

  handleOpenModal = () => {
    this.setState({ openModal: true });
  }

  handleCloseModal = () => {
    this.setState({ openModal: false });
  }

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  }

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState({ isLoading: true, submitStatus: { type: null, message: '' } });

    // Keep the console log as requested
    console.log('Form submitted:', this.state.formData);

    // EmailJS service configuration
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // Check if credentials are loaded
    if (!serviceId || !templateId || !publicKey) {
      this.setState({
        submitStatus: {
          type: 'error',
          message: 'EmailJS configuration is missing. Please check your .env file.'
        },
        isLoading: false
      });
      return;
    }

    // Prepare template parameters
    const templateParams = {
      from_name: this.state.formData.name,
      health_goals: this.state.formData.healthGoals,
      trainer_selection: this.state.formData.trainerSelection,
      phone_number: this.state.formData.phoneNumber,
      best_time_to_call: this.state.formData.bestTimeToCall,
    };

    // Send email using EmailJS
    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('Trainer selection email sent successfully:', response);
        this.setState({
          submitStatus: { type: 'success', message: 'Thank you! Your trainer selection request has been sent successfully.' },
          formData: {
            name: '',
            healthGoals: '',
            trainerSelection: '',
            phoneNumber: '',
            bestTimeToCall: ''
          },
          openModal: false,
          isLoading: false
        });
        // Redirect to external link after successful submission
        window.location.href = this.state.externalLink;
      })
      .catch((error) => {
        console.error('Failed to send trainer selection email:', error);
        this.setState({
          submitStatus: {
            type: 'error',
            message: `Failed to send email: ${error.text || 'Please check your configuration and try again.'}`
          },
          isLoading: false
        });
      });
  }

  render() {
    const { isMobile, theme } = this.props;
    const { openModal, formData, isLoading, submitStatus } = this.state;

    const trainerOptions = [
      { value: '', label: 'Select a trainer' },
      ...trainers.map((trainer, index) => ({
        value: `trainer${index + 1}`,
        label: trainer.name
      }))
    ];

    const timeOptions = [
      { value: '', label: 'Select a time' },
      { value: 'morning', label: 'Morning (6 AM - 12 PM)' },
      { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
      { value: 'evening', label: 'Evening (5 PM - 9 PM)' }
    ];

    const services = [
      {
        icon: PersonIcon,
        title: 'Personal Training Membership',
        subtitle: '1-on-1 & Small Group Sessions',
        description: 'Get personalized attention with our certified trainers who create custom workout plans tailored to your specific goals and fitness level.',
        features: [
          'Customized workout plans',
          'Proper form instruction',
          'Goal-specific programming',
          'Progress tracking',
          'Flexible scheduling',
          'Trainer-exclusive gym access: $30',
          'Annual $25 administration fee'
        ],
        pricing: 'Choose a Trainer',
        popular: true,
        color: 'primary.main',
        buttonLabel:'Personal Trainer Membership',
        externalLink: 'https://varsity-fitness.exercise.com/packages/63528/purchase/',
        firstButtonModal: true

      },
      {
        icon: FitnessIcon,
        title: 'Gym Access',
        subtitle: 'Train on Your Schedule',
        description: 'Access our fully equipped gym with state-of-the-art equipment whenever it fits your schedule.',
        features: [
          'Personal access codes',
          'Modern equipment',
          'Free weights & machines',
          'Cardio equipment',
          'Functional training area',
          '$50/month',
          'Annual $25 administration fee',


        ],
        pricing: '$50/month',
        popular: true,
        color: 'secondary.main',
        buttonLabel:'Join Now',
      
        externalLink: 'https://varsity-fitness.exercise.com/packages/61874/purchase/',
        firstButtonModal: false
      }
    ];

    const membershipPlans = [
      {
        name: 'Starter',
        price: '$49',
        period: '/month',
        originalPrice: null,
        description: 'Perfect for self-motivated individuals who want basic gym access',
        features: [
          '24/7 gym access',
          'Basic equipment use',
          'Locker room facilities',
          'Free initial fitness assessment',
          'Access to mobile app',
          'Basic workout tracking'
        ],
        notIncluded: [
          'Personal training sessions',
          'Group classes',
          'Nutritional guidance',
          'Guest passes'
        ],
        buttonText: 'Get Started',
        popular: false,
        color: 'primary.main'
      },
      {
        name: 'Complete',
        price: '$99',
        period: '/month',
        originalPrice: '$129',
        description: 'Our most popular plan with comprehensive fitness support',
        features: [
          'Everything in Starter',
          'Unlimited group classes',
          '1 personal training session/month',
          'Nutritional consultation',
          'Progress tracking & analytics',
          'Priority equipment access',
          'Workout plan customization',
          'Email support'
        ],
        notIncluded: [
          'Weekly personal training',
          'Custom meal planning',
          'Guest passes'
        ],
        buttonText: 'Most Popular',
        popular: true,
        color: 'secondary.main'
      },
      {
        name: 'Elite',
        price: '$199',
        period: '/month',
        originalPrice: '$249',
        description: 'Premium experience with maximum personal attention',
        features: [
          'Everything in Complete',
          '4 personal training sessions/month',
          'Custom meal planning',
          'Weekly progress reviews',
          'Priority class booking',
          '2 guest passes per month',
          'Supplement discounts (15%)',
          'Phone & email support',
          'Exclusive member events'
        ],
        notIncluded: [],
        buttonText: 'Go Elite',
        popular: false,
        color: 'primary.main'
      }
    ];

    const comparisonFeatures = [
      { feature: '24/7 Gym Access', starter: true, complete: true, elite: true },
      { feature: 'Locker Room Access', starter: true, complete: true, elite: true },
      { feature: 'Free Fitness Assessment', starter: true, complete: true, elite: true },
      { feature: 'Mobile App Access', starter: true, complete: true, elite: true },
      { feature: 'Group Classes', starter: false, complete: true, elite: true },
      { feature: 'Personal Training Sessions', starter: false, complete: '1/month', elite: '4/month' },
      { feature: 'Nutritional Guidance', starter: false, complete: 'Basic', elite: 'Custom Plans' },
      { feature: 'Progress Tracking', starter: 'Basic', complete: 'Advanced', elite: 'Premium' },
      { feature: 'Guest Passes', starter: false, complete: false, elite: '2/month' },
      { feature: 'Priority Booking', starter: false, complete: false, elite: true },
      { feature: 'Supplement Discounts', starter: false, complete: false, elite: '15%' },
      { feature: 'Phone Support', starter: false, complete: false, elite: true }
    ];

    return (
      <Box>
        {/* Hero Section */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 600,
                  mb: 3,
                  maxWidth: '100%',
                  textAlign: 'center'
                }}
              >
                 Pricing and Services <Box component="span" sx={{ color: theme.palette.primary.main }}></Box><br />
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 600, mx: 'auto' }}>
              Comprehensive fitness solutions with transparent pricing. Choose the membership that fits your lifestyle and budget. No hidden fees, no surprises.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                ✓ Expert Personal Training
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                ✓ Transparent Pricing
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Services */}
        <Box sx={{ backgroundColor: 'white', pb: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} sx={{ mb: 10, justifyContent: 'center', pt:4}}>
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <Grid item xs={12} md={6} lg={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Paper
                      sx={{
                        height: '100%',
                        maxWidth: 500,
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        borderColor: 'grey.200',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        mx: 'auto'
                      }}
                    >

                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Service Icon */}
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: service.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            mx: 'auto'
                          }}
                        >
                          <IconComponent sx={{ fontSize: 40, color: 'white' }} />
                        </Box>

                        {/* Service Title */}
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            textAlign: 'center',
                            color: 'text.primary'
                          }}
                        >
                          {service.title}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: service.color,
                            fontWeight: 500,
                            mb: 2,
                            textAlign: 'center'
                          }}
                        >
                          {service.subtitle}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                            textAlign: 'center',
                            flexGrow: 1
                          }}
                        >
                          {service.description}
                        </Typography>

                        {/* Features List */}
                        <List dense sx={{ mb: 3 }}>
                          {service.features.map((feature, featureIndex) => (
                            <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ fontSize: 20, color: service.color }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                sx={{
                                  '& .MuiTypography-root': {
                                    fontSize: '0.9rem',
                                    color: 'text.secondary'
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
                        {service.firstButtonModal ? (
                          <Button
                            onClick={this.handleOpenModal}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              color: 'primary.main !important',
                              borderColor: 'primary.main !important',
                              textDecoration: 'none !important',
                              borderRadius: 2,
                              '&:hover': {
                                borderColor: 'primary.main !important',
                                backgroundColor: 'rgba(178, 35, 53, 0.1) !important',
                                textDecoration: 'none !important'
                              },
                              '&:active': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:visited': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:focus': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              }
                            }}
                          >
                            {service.buttonLabel}
                          </Button>
                        ) : (
                          <Button
                            component="a"
                            href={service.externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              color: 'primary.main !important',
                              borderColor: 'primary.main !important',
                              textDecoration: 'none !important',
                              borderRadius: 2,
                              '&:hover': {
                                borderColor: 'primary.main !important',
                                backgroundColor: 'rgba(178, 35, 53, 0.1) !important',
                                textDecoration: 'none !important'
                              },
                              '&:active': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:visited': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:focus': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              }
                            }}
                          >
                            {service.buttonLabel}
                          </Button>
                        )}
                        {service.buttonLabel2 && service.externalLink && (
                          <Button
                            component="a"
                            href={service.externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              color: 'primary.main !important',
                              borderColor: 'primary.main !important',
                              textDecoration: 'none !important',
                              borderRadius: 2,
                              '&:hover': {
                                borderColor: 'primary.main !important',
                                backgroundColor: 'rgba(178, 35, 53, 0.1) !important',
                                textDecoration: 'none !important'
                              },
                              '&:active': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:visited': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              },
                              '&:focus': {
                                color: 'primary.main !important',
                                textDecoration: 'none !important'
                              }
                            }}
                          >
                            {service.buttonLabel2}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Modal for Trainer Selection */}
        <Dialog 
          open={openModal} 
          onClose={this.handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              pt: 4,
              pb: 2
            }}
          >
            <Box
              component="img"
              src={require('../assets/images/varsityFile.png')}
              alt="Varsity Fitness Logo"
              sx={{
                height: 60,
                width: 'auto'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Choose Your Trainer
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box component="form" onSubmit={this.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Name */}
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={this.handleInputChange}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                    }
                  }
                }}
              />

              {/* Physical Health Goals */}
              <FormControl fullWidth required>
                <InputLabel>Physical Health Goals</InputLabel>
                <Select
                  name="healthGoals"
                  value={formData.healthGoals}
                  onChange={this.handleInputChange}
                  label="Physical Health Goals"
                  sx={{
                    borderRadius: 2,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                    }
                  }}
                >
                  <MenuItem value="">Select a goal</MenuItem>
                  <MenuItem value="weight-loss">Weight Loss</MenuItem>
                  <MenuItem value="muscle-gain">Muscle Gain</MenuItem>
                  <MenuItem value="strength">Build Strength</MenuItem>
                  <MenuItem value="endurance">Improve Endurance</MenuItem>
                  <MenuItem value="flexibility">Flexibility & Mobility</MenuItem>
                  <MenuItem value="overall">Overall Health & Fitness</MenuItem>
                </Select>
              </FormControl>

              {/* Personal Trainer Selection */}
              <FormControl fullWidth required>
                <InputLabel>Select a Trainer</InputLabel>
                <Select
                  name="trainerSelection"
                  value={formData.trainerSelection}
                  onChange={this.handleInputChange}
                  label="Select a Trainer"
                  sx={{
                    borderRadius: 2,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                    }
                  }}
                >
                  {trainerOptions.map((trainer) => (
                    <MenuItem key={trainer.value} value={trainer.value}>
                      {trainer.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Phone Number */}
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={this.handleInputChange}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                    }
                  }
                }}
              />

              {/* Best Time to Call */}
              <FormControl fullWidth required>
                <InputLabel>Best Time to Call</InputLabel>
                <Select
                  name="bestTimeToCall"
                  value={formData.bestTimeToCall}
                  onChange={this.handleInputChange}
                  label="Best Time to Call"
                  sx={{
                    borderRadius: 2,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                    }
                  }}
                >
                  {timeOptions.map((time) => (
                    <MenuItem key={time.value} value={time.value}>
                      {time.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Status Message */}
            {submitStatus.type && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography
                  sx={{
                    color: submitStatus.type === 'success' ? 'green' : 'red',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                >
                  {submitStatus.message}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={this.handleCloseModal}
              sx={{
                color: theme.palette.grey[600],
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleSubmit}
              variant="contained"
              disabled={isLoading}
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                px: 3,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark
                },
                '&:disabled': {
                  backgroundColor: 'rgba(178, 35, 53, 0.5)',
                  cursor: 'not-allowed'
                }
              }}
            >
              {isLoading ? 'Sending...' : 'Get Started'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default withHooks(ServicePricingPage);
